import { create, windowScheduler, keyResolver } from "@yornaath/batshit";
import apiClient from "services/ApiClient";
import { createQueryString } from "helpers/queryString";
import { BATCH_WINDOW_MS } from "helpers/batcher";
import { PrefixId } from "features/Resources/Shared/models";
import { ActionIssuesResolvedResponse } from "./models";

// Middleware converts snake_case to camelCase, so "email_19" becomes "email19"
function toCamelCaseKey(prefixId: PrefixId): string {
  return prefixId.replace(/_/g, "");
}

const DEFAULT_RESOLVED_RESPONSE: Omit<ActionIssuesResolvedResponse, "prefixId"> = {
  nonTiming: {
    resolved: true,
    unresolvedCount: 0,
    totalCount: 0,
  },
  undeliveredIssueResolved: true,
};

const actionIssuesResolvedBatcher = create({
  fetcher: async (prefixIds: PrefixId[]): Promise<ActionIssuesResolvedResponse[]> => {
    const queryString = createQueryString({
      additionalQueries: {
        action_prefix_ids: prefixIds,
      },
    });

    // Backend returns { data: { email19: {...}, message32: {...} } }
    const { payload } = await apiClient.performRequest<{ data: Record<string, ActionIssuesResolvedResponse> }>(
      `/api/actions/issues/resolved/batch?${queryString}`,
    );

    const results = payload.data;

    // Convert object to array in the same order as prefixIds
    // Note: Middleware converts "email_19" to "email19", so we need to use camelCase keys
    return prefixIds.map((prefixId) => {
      const camelCaseKey = toCamelCaseKey(prefixId);
      const result = results[camelCaseKey];

      // If backend didn't return data for this action, default to resolved
      if (!result) {
        return {
          ...DEFAULT_RESOLVED_RESPONSE,
          prefixId,
        };
      }

      return result;
    });
  },
  resolver: keyResolver("prefixId"),
  scheduler: windowScheduler(BATCH_WINDOW_MS),
});

export default actionIssuesResolvedBatcher;
